import { ActivityModel } from './ActivityModel'
import { DateTime } from 'luxon'
import { escape } from '@/src/server/helpers/leverade-api'
import { ActivityTypeModel } from '@/src/server/modules/activities/ActivityTypeModel'

export class ActivitiesService {
  axios

  constructor ({ axios }) {
    this.axios = axios
  }

  async fetchActivities (managerId, filters) {
    const params = {
      sort: 'computed_start_date',
      'page[size]': 500,
      include: 'activitytype,disciplines',
    }

    if (filters) {
      const query = this.generateFilterQuery(filters)

      if (query) {
        params.filter = query
      }
    }

    const { data: { data: activities, included } } = await this.axios(`v2/managers/${managerId}/activities`, {
      params,
    })

    let activityTypes = []

    if (included) {
      activityTypes = included.filter((includedItem) => includedItem.type === 'activitytype')
        .map(this.toActivityTypeModel)
    }

    return activities.map((activity) => this.toActivityModel(activity, activityTypes))
  }

  async fetchActivity (activityId) {
    const params = {
      include: 'disciplines',
    }

    const { data: { data: activity } } = await this.axios(`/v2/activities/${activityId}`, {
      params,
    })

    let activityTypes = []

    return this.toActivityModel(activity, activityTypes)
  }

  async fetchFeaturedActivities (managerId, activitiesAmount) {
    const params = {
      sort: 'computed_start_date',
      'page[size]': activitiesAmount,
      include: 'activitytype,disciplines',
      filter: 'featured = true',
    }

    const { data: { data: activities, included } } = await this.axios(`v2/managers/${managerId}/activities`, {
      params,
    })

    let activityTypes = []

    if (included) {
      activityTypes = included.filter((includedItem) => includedItem.type === 'activitytype')
        .map(this.toActivityTypeModel)
    }

    return activities.map((activity) => this.toActivityModel(activity, activityTypes))
  }

  toActivityModel (activity, activityTypes) {
    const disciplinesRelation = activity.relationships.disciplines.data
    const activityTypeRelation = activity.relationships.activitytype.data

    let activityType = null
    let disciplineIds = []

    if (activityTypeRelation) {
      activityType = activityTypes.find((activityType) => activityType.id === activityTypeRelation.id)
    }

    if (disciplinesRelation && Array.isArray(disciplinesRelation)) {
      disciplineIds = disciplinesRelation.map(discipline => discipline.id)
    }

    if (activity.meta && activity.meta.competition_data) {
      let startDate = activity.meta.competition_data.start_date
      let endDate = activity.meta.competition_data.end_date

      activity.meta.competition_data.start_date = startDate ? DateTime.fromISO(startDate) : null
      activity.meta.competition_data.end_date = endDate ? DateTime.fromISO(endDate) : null
    }

    if (activity.meta && activity.meta.custom_files) {
      activity.meta.custom_files.forEach(file => {
        let date = file.created_at

        file.created_at = date ? DateTime.fromISO(date) : null
      })
    }

    return new ActivityModel({
      id: activity.id,
      name: activity.attributes.name,
      startDate: activity.attributes.start_date ? DateTime.fromISO(activity.attributes.start_date) : null,
      endDate: activity.attributes.end_date ? DateTime.fromISO(activity.attributes.end_date) : null,
      place: activity.attributes.place,
      country: activity.attributes.country,
      url: activity.attributes.url,
      disciplineIds,
      activityType,
      selection: activity.attributes.selection,
      number: activity.attributes.number,
      description: activity.attributes.description,
      sourceType: activity.attributes.source_type,
      meta: activity.meta,
    })
  }

  toActivityTypeModel (activityType) {
    return new ActivityTypeModel({
      id: activityType.id,
      name: activityType.attributes.name,
    })
  }

  generateFilterQuery (filters) {
    const querySlices = [
      this.generateFilterQuerySelection(filters.selection),
      this.generateFilterQueryStartDate(filters.startDate),
      this.generateFilterQueryEndDate(filters.endDate),
      this.generateFilterQueryDisciplineIds(filters.disciplineIds),
      this.generateFilterQueryActivityTypeId(filters.activityTypeIds),
    ]

    return querySlices.filter(slice => slice).join(' AND ')
  }

  generateFilterQuerySelection (selection) {
    if (![true, false].includes(selection)) {
      return
    }

    const value = selection === true ? 'true' : 'false'

    const filterParts = [
      `(source_type = "manual" AND selection = ${value})`,
      `(source_type = "competition" AND competition.selection = ${value})`,
    ]

    return `(${filterParts.join(' OR ')})`
  }

  generateFilterQueryStartDate (startDate) {
    if (startDate) {
      const isoStartDate = startDate.toUTC().toISO({ suppressMilliseconds: true })
      const ymdStartDate = startDate.toUTC().toFormat('yyyy-MM-dd')

      const filterParts = [
        `(source_type = "manual" AND (start_date >= ${isoStartDate} OR end_date >= ${isoStartDate}))`,
        `(source_type = "competition" AND ` +
        `(competition.start_date >= ${ymdStartDate} OR competition.end_date >= ${ymdStartDate}))`,
      ]

      return `(${filterParts.join(' OR ')})`
    }
  }

  generateFilterQueryEndDate (endDate) {
    if (endDate) {
      const isoEndDate = endDate.toUTC().toISO({ suppressMilliseconds: true })
      const ymdEndDate = endDate.toUTC().toFormat('yyyy-MM-dd')

      const filterParts = [
        `(source_type = "manual" AND (start_date <= ${isoEndDate} OR end_date <= ${isoEndDate}))`,
        `(source_type = "competition" AND ` +
        `(competition.start_date <= ${ymdEndDate} OR competition.end_date <= ${ymdEndDate}))`,
      ]

      return `(${filterParts.join(' OR ')})`
    }
  }

  generateFilterQueryDisciplineIds (disciplineIds) {
    if (disciplineIds && disciplineIds.length) {
      const serializedDisciplineIds = disciplineIds.map(escape).join(', ')

      const filterParts = [
        `(source_type = "manual" AND activitydisciplines.discipline.id IN (${serializedDisciplineIds}))`,
        `(source_type = "competition" AND competition.discipline.id IN (${serializedDisciplineIds}))`,
      ]

      return `(${filterParts.join(' OR ')})`
    }
  }

  generateFilterQueryActivityTypeId (activityTypeIds) {
    if (activityTypeIds && activityTypeIds.length) {
      const serializedActivityTypeIds = activityTypeIds.map(escape).join(', ')

      return `(activitytype.id IN (${serializedActivityTypeIds}))`
    }
  }
}



// WEBPACK FOOTER //
// ./src/server/modules/activities/ActivitiesService.js